import {
  appendFailure,
  deleteMedia,
  dueActions,
  getMedia,
  listDueAiCampaigns,
  upsertAiCampaign,
  upsertScheduledAction,
  type ScheduledActionRow,
} from '../cache/db'
import { api, withFreshAccess } from '../api/client'
import { useAccounts } from '../store/accounts'
import { useScheduler } from '../store/scheduler'
import { nextRunAt, runAiCampaign } from './aiCampaign'
import { tickAutoFollowCampaigns } from './autoFollow'
import { compressImageToFit } from '../api/imageCompress'

const TICK_MS = 30_000

let stopped = false
let timer: ReturnType<typeof setTimeout> | null = null

/**
 * Browser-side scheduler. Runs every TICK_MS and fires every action whose
 * runAt has elapsed. The worker is stateless — all persistence here is
 * IndexedDB.
 *
 * IMPORTANT: this only runs while a tab is open. We may upgrade this to a
 * service worker with periodic background sync later, but the user
 * approved a browser-only scheduler for now.
 */
export function startScheduler(): () => void {
  stopped = false
  schedule(0)
  return () => {
    stopped = true
    if (timer) clearTimeout(timer)
    timer = null
  }
}

function schedule(ms: number) {
  if (timer) clearTimeout(timer)
  timer = setTimeout(tick, ms)
}

async function tick() {
  if (stopped) return
  // Stamp the heartbeat at the TOP of the tick — before any awaits —
  // so the UI can prove the runner is alive even when a tick stalls
  // mid-action.
  useScheduler.getState().setLastTick(Date.now())
  try {
    const now = Date.now()
    const due = await dueActions(now)
    for (const action of due) {
      if (stopped) break
      await runAction(action)
    }
    // Auto-follow campaigns: discover N actors by tag, queue follows
    // into scheduled_actions with cadence-spaced runAts so the runner
    // above naturally enforces the rate limit. Runs ahead of the
    // dueActions loop next tick so anything queued here gets a fair
    // shake.
    try {
      await tickAutoFollowCampaigns(now)
    } catch (e) {
      console.error('[scheduler] auto-follow tick failed', e)
    }
    // AI campaigns: a separate concept from one-off scheduled actions.
    // Each due campaign runs the full search → score → like/repost
    // pipeline, then reschedules itself with random minute jitter.
    const dueCampaigns = await listDueAiCampaigns(now)
    for (const camp of dueCampaigns) {
      if (stopped) break
      let lastError: string | undefined
      let newSeenUris: string[] = []
      try {
        const { run } = await runAiCampaign(camp)
        newSeenUris = run.evaluations.map((e) => e.postUri)
        if (run.error) {
          lastError = run.error
        } else {
          const failed = run.evaluations.filter((e) => e.executed === false)
          if (failed.length) {
            lastError = `${failed.length} action(s) failed: ${failed[0].executeError ?? 'unknown error'}`
          }
        }
      } catch (e) {
        lastError = e instanceof Error ? e.message : String(e)
        console.error('[scheduler] campaign failed', camp.id, e)
      }
      const mergedSeen = [...new Set([...(camp.seenPostUris ?? []), ...newSeenUris])]
      const updated = {
        ...camp,
        lastRunAt: now,
        nextRunAt: nextRunAt(camp, now),
        lastError,
        seenPostUris: mergedSeen.length > 2000 ? mergedSeen.slice(-2000) : mergedSeen,
        updatedAt: Date.now(),
      }
      await upsertAiCampaign(updated)
    }
  } catch (err) {
    console.error('[scheduler] tick failed', err)
  } finally {
    if (!stopped) schedule(TICK_MS)
  }
}

export async function runAction(action: ScheduledActionRow): Promise<void> {
  const accounts = useAccounts.getState().accounts
  const acc = accounts.find((a) => a.did === action.accountDid || a.id === action.accountDid)
  if (!acc) {
    await markFailed(action, 'owning account not signed in on this device')
    return
  }
  await upsertScheduledAction({ ...action, status: 'running', updatedAt: Date.now() })

  try {
    const result = await withFreshAccess(acc.id, async () => {
      switch (action.kind) {
        case 'like':
          return await api.xrpc<{ uri: string; cid?: string }>({
            accountId: acc.id,
            path: 'com.atproto.repo.createRecord',
            body: {
              repo: acc.did,
              collection: 'app.bsky.feed.like',
              record: {
                $type: 'app.bsky.feed.like',
                subject: { uri: action.subject, cid: action.subjectCid ?? '' },
                createdAt: new Date().toISOString(),
              },
            },
          })
        case 'repost':
          return await api.xrpc<{ uri: string; cid?: string }>({
            accountId: acc.id,
            path: 'com.atproto.repo.createRecord',
            body: {
              repo: acc.did,
              collection: 'app.bsky.feed.repost',
              record: {
                $type: 'app.bsky.feed.repost',
                subject: { uri: action.subject, cid: action.subjectCid ?? '' },
                createdAt: new Date().toISOString(),
              },
            },
          })
        case 'follow':
          return await api.xrpc<{ uri: string; cid?: string }>({
            accountId: acc.id,
            path: 'com.atproto.repo.createRecord',
            body: {
              repo: acc.did,
              collection: 'app.bsky.graph.follow',
              record: {
                $type: 'app.bsky.graph.follow',
                subject: action.subject,
                createdAt: new Date().toISOString(),
              },
            },
          })
        case 'unfollow':
          // action.subject is the follow record URI to delete
          return await api.xrpc<{ ok?: boolean }>({
            accountId: acc.id,
            path: 'com.atproto.repo.deleteRecord',
            body: parseAtUriParts(action.subject, acc.did),
          })
        case 'post':
        case 'reply': {
          const embed = await buildEmbedFromMediaIds(acc.id, action.mediaIds ?? [])
          const record: Record<string, unknown> = {
            $type: 'app.bsky.feed.post',
            text: action.text ?? '',
            createdAt: new Date().toISOString(),
          }
          if (embed) record.embed = embed
          if (action.kind === 'reply' && action.replyParentUri) {
            record.reply = {
              parent: {
                uri: action.replyParentUri,
                cid: action.replyParentCid ?? '',
              },
              root: {
                uri: action.replyRootUri ?? action.replyParentUri,
                cid: action.replyRootCid ?? action.replyParentCid ?? '',
              },
            }
          }
          const created = await api.xrpc<{ uri: string; cid?: string }>({
            accountId: acc.id,
            path: 'com.atproto.repo.createRecord',
            body: {
              repo: acc.did,
              collection: 'app.bsky.feed.post',
              record,
            },
          })
          // Now that the post landed, drop the local media bytes — we
          // don't need them anymore and they're potentially large.
          for (const id of action.mediaIds ?? []) {
            await deleteMedia(id).catch(() => undefined)
          }
          return created
        }
      }
    })
    const resultUri =
      result && typeof result === 'object' && 'uri' in result
        ? (result as { uri?: string }).uri
        : undefined
    await upsertScheduledAction({
      ...action,
      status: 'done',
      resultUri,
      updatedAt: Date.now(),
    })
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err)
    const httpStatus = inferStatus(reason)
    await markFailed(action, reason, httpStatus)
  }
}

async function markFailed(
  action: ScheduledActionRow,
  reason: string,
  httpStatus?: number,
) {
  const ts = Date.now()
  await upsertScheduledAction({
    ...action,
    status: 'failed',
    failureReason: reason,
    updatedAt: ts,
  })
  await appendFailure({
    id: `${action.id}:${ts}`,
    actionId: action.id,
    accountDid: action.accountDid,
    kind: action.kind,
    subject: action.subject,
    reason,
    httpStatus,
    occurredAt: ts,
  })
}

function inferStatus(msg: string): number | undefined {
  const m = msg.match(/\b(4\d\d|5\d\d)\b/)
  if (!m) return undefined
  return Number(m[1])
}

function parseAtUriParts(
  uri: string,
  fallbackRepo: string,
): { repo: string; collection: string; rkey: string } {
  // at://<repo>/<collection>/<rkey>
  const m = uri.match(/^at:\/\/([^/]+)\/([^/]+)\/([^/]+)$/)
  if (!m) return { repo: fallbackRepo, collection: 'app.bsky.graph.follow', rkey: uri }
  return { repo: m[1], collection: m[2], rkey: m[3] }
}

/**
 * Read each scheduled_media row, upload the Blob to the user's PDS via
 * com.atproto.repo.uploadBlob, and assemble the right embed shape:
 *   - all images → app.bsky.embed.images
 *   - exactly one video → app.bsky.embed.video
 * Returns null if there are no media rows.
 */
async function buildEmbedFromMediaIds(
  accountId: string,
  mediaIds: string[],
): Promise<Record<string, unknown> | null> {
  if (mediaIds.length === 0) return null
  const rows = await Promise.all(mediaIds.map((id) => getMedia(id)))
  const found = rows.filter((r): r is NonNullable<typeof r> => !!r)
  if (found.length === 0) return null
  const hasVideo = found.some((r) => r.mime.startsWith('video/'))
  if (hasVideo) {
    const v = found.find((r) => r.mime.startsWith('video/'))!
    const ref = await api.uploadBlob({ accountId, blob: v.blob })
    return {
      $type: 'app.bsky.embed.video',
      video: ref,
      alt: v.alt,
      ...(v.aspectRatio ? { aspectRatio: v.aspectRatio } : {}),
    }
  }
  const images = []
  for (const m of found) {
    if (!m.mime.startsWith('image/')) continue
    // Safety net: bsky PDS rejects images > 1 MB. Compose now
    // compresses up-front, but legacy queued items + edge cases
    // (e.g. PNG within budget but in an odd format) still flow
    // through; re-encode here if we're over budget.
    let uploadBlob: Blob = m.blob
    let aspect = m.aspectRatio
    if (m.blob.size > 950_000) {
      try {
        const r = await compressImageToFit(m.blob)
        uploadBlob = r.blob
        if (r.width && r.height) aspect = { width: r.width, height: r.height }
      } catch {
        // Fall through with the original blob; PDS will throw a
        // clearer error that the failure log can surface.
      }
    }
    const ref = await api.uploadBlob({ accountId, blob: uploadBlob })
    images.push({
      image: ref,
      alt: m.alt ?? '',
      ...(aspect ? { aspectRatio: aspect } : {}),
    })
  }
  if (images.length === 0) return null
  return { $type: 'app.bsky.embed.images', images }
}
